/**
 * Filters a string to only allow numeric characters with at most one
 * decimal separator (comma or dot). First character must be a digit.
 */
export const filterNumeric = (value: string): string => {
  let hasDecimal = false;
  return value
    .split('')
    .filter((ch, i) => {
      if (/[0-9]/.test(ch)) return true;
      if ((ch === ',' || ch === '.') && !hasDecimal && i > 0) {
        hasDecimal = true;
        return true;
      }
      return false;
    })
    .join('');
};

/**
 * Normalizes a numeric string by replacing comma with dot for parsing.
 */
export const normalizeNumericString = (value: string): string => {
  // If a comma is present it is the Danish decimal separator,
  // so any dots must be thousands separators — strip them first.
  if (value.includes(',')) {
    return value.replace(/\./g, '').replace(',', '.');
  }
  // Danish integers formatted with dot thousands separators (e.g. "1.234" or "1.234.567").
  // The pattern is: 1-3 leading digits followed by one or more groups of exactly 3 digits
  // separated by dots — this can never be a decimal dot.
  if (/^\d{1,3}(\.\d{3})+$/.test(value)) {
    return value.replace(/\./g, '');
  }
  return value;
};

/**
 * Formats a number for display in a text field (dot → comma).
 * Returns empty string for null/undefined.
 */
export const formatNumberInput = (value?: number | null): string => {
  if (value === undefined || value === null) return '';
  return String(value).replace('.', ',');
};

/**
 * Parses a comma-formatted numeric string to a number.
 * Returns 0 if the value is not a valid number.
 */
export const parseNumberInput = (value: string): number => {
  const parsed = Number(normalizeNumericString(value));
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Parses a comma-formatted numeric string to a number or null.
 * Returns null for empty/invalid values.
 */
export const parseOptionalNumberInput = (value?: string): number | null => {
  if (!value) return null;
  const parsed = Number(normalizeNumericString(value));
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Validates if a string is a valid non-zero numeric input.
 */
export const isValidNumeric = (value: string | undefined): boolean => {
  if (!value) return false;
  const normalized = normalizeNumericString(value);
  const num = Number(normalized);
  return Number.isFinite(num) && num !== 0;
};
